import { useQuery } from '@tanstack/react-query';
import type { ColumnSort } from '@tanstack/react-table';

import type {
  TrackReleaseType,
  TrackRoleType,
} from '~/modules/artist-roster/hooks/useReleasesUrlParams/types';

import { DEFAULT_PAGE_SIZE } from '../components/constants';
import { getArtistTracks } from '../lib/tracks';
import {
  formatArtistTracksSortBy,
  formatContributorRole,
  formatReleaseDate,
} from '../lib/utils';

interface ArtistTracksQueryProps {
  uaid?: number | null;
  columnSort?: ColumnSort;
  pageNumber: number;
  searchText?: string;
  releaseType?: TrackReleaseType;
  limit?: number;
  isReleased?: boolean;
  role?: TrackRoleType;
}

export function useArtistTracksQuery({
  uaid,
  columnSort,
  pageNumber,
  searchText,
  releaseType,
  limit = DEFAULT_PAGE_SIZE,
  isReleased,
  role,
}: ArtistTracksQueryProps) {
  return useQuery({
    enabled: !!uaid,
    queryKey: [
      'artist-tracks',
      uaid,
      pageNumber,
      columnSort?.id,
      columnSort?.desc,
      releaseType,
      searchText,
      limit,
      isReleased,
      role,
    ],
    queryFn: () => {
      const sortBy = formatArtistTracksSortBy(columnSort);
      const { startDate, endDate } = formatReleaseDate(releaseType);
      const contributorType = formatContributorRole(role);

      return getArtistTracks({
        offset: (pageNumber - 1) * limit,
        limit,
        uaid,
        sortBy,
        startDate,
        endDate,
        searchText,
        isReleased,
        contributorType,
      });
    },
  });
}
